import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import {
  Users, UserPlus, PhoneCall, CalendarCheck, CheckCircle2, AlertCircle,
  TrendingUp, DollarSign, Activity,
} from "lucide-react";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

const PIPELINE_COLORS = {
  New: "#2563eb", Contacted: "#0284c7", Qualified: "#8b5cf6",
  "Appointment Booked": "#d97706", "Job Completed": "#059669", Lost: "#94a3b8",
};

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-3xl font-bold font-heading mt-1">{value}</p>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { business } = useAuth();
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/stats?range=${range}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [range]);

  const fmt = (n) => "$" + Number(n || 0).toLocaleString();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Welcome back{business?.owner_name ? `, ${business.owner_name.split(" ")[0]}` : ""}</h1>
          <p className="text-slate-500 mt-1">Here's how {business?.name || "your business"} is doing.</p>
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="dashboard-date-filter">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${range === r.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={UserPlus} label="New leads" value={data.new_leads} accent="bg-blue-50 text-blue-600" testid="stat-new-leads" />
            <StatCard icon={Users} label="Total leads" value={data.total_leads} accent="bg-indigo-50 text-indigo-600" testid="stat-total-leads" />
            <StatCard icon={PhoneCall} label="Contacted" value={data.contacted_leads} accent="bg-sky-50 text-sky-600" testid="stat-contacted" />
            <StatCard icon={CalendarCheck} label="Appointments" value={data.appointments} accent="bg-amber-50 text-amber-600" testid="stat-appointments" />
            <StatCard icon={CheckCircle2} label="Jobs completed" value={data.jobs_completed} accent="bg-emerald-50 text-emerald-600" testid="stat-jobs" />
            <StatCard icon={AlertCircle} label="Needs follow-up" value={data.follow_up_needed} accent="bg-red-50 text-red-600" testid="stat-followup" />
            <StatCard icon={TrendingUp} label="Conversion rate" value={`${data.conversion_rate}%`} accent="bg-violet-50 text-violet-600" testid="stat-conversion" />
            <StatCard icon={DollarSign} label="Opportunity value" value={fmt(data.opportunity_value)} accent="bg-emerald-50 text-emerald-600" testid="stat-opportunity" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6 border-slate-200 lg:col-span-2">
              <h3 className="font-heading font-semibold text-lg mb-4">Lead volume</h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.trend}>
                  <defs>
                    <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} stroke="#94a3b8" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Area type="monotone" dataKey="leads" stroke="#2563eb" strokeWidth={2} fill="url(#leadGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 border-slate-200">
              <h3 className="font-heading font-semibold text-lg mb-4">Pipeline</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.pipeline} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis type="category" dataKey="status" width={110} tick={{ fontSize: 11 }} stroke="#64748b" />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {data.pipeline.map((p) => <Cell key={p.status} fill={PIPELINE_COLORS[p.status]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Activity className="h-5 w-5 text-blue-600" /> Recent activity</h3>
              <Link to="/app/leads"><Button variant="ghost" size="sm">View leads</Button></Link>
            </div>
            {data.recent_activity.length === 0 ? (
              <p className="text-slate-400 text-sm py-6 text-center" data-testid="activity-empty">No activity yet. Add a lead or share your booking form to get started.</p>
            ) : (
              <div className="space-y-3">
                {data.recent_activity.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 text-sm" data-testid="activity-item">
                    <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-slate-700"><span className="font-medium">{a.actor}</span> {a.action}</span>
                    <span className="text-slate-400 ml-auto whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
